import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartUpdateEvent } from '@theme/events';

class CartModelChangeDialog extends DialogComponent {
  #contexts = new Map();
  #context = null;
  #lastTrigger = null;
  #busy = false;
  #observer = null;
  #injectQueued = false;

  connectedCallback() {
    super.connectedCallback();
    this.#refresh();
    document.addEventListener('click', this.#handleDocumentClick);
    this.addEventListener('click', this.#handleDialogClick);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);

    const cartComponent = this.closest('cart-items-component');
    if (cartComponent) {
      this.#observer = new MutationObserver(() => this.#scheduleRefresh());
      this.#observer.observe(cartComponent, { childList: true, subtree: true });
    }

    this.#scheduleRefresh();
    requestAnimationFrame(() => this.#scheduleRefresh());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener('click', this.#handleDocumentClick);
    this.removeEventListener('click', this.#handleDialogClick);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
  }

  // This intentionally overrides DialogComponent.closeDialog for the cart model
  // switcher only. The older Horizon dialog close routine calls getAnimations()
  // and other animation APIs that can throw Safari/WebKit's generic
  // "The string did not match the expected pattern" DOMException. A model change
  // is a transactional cart action, so closing this dialog should be simple and
  // must never be allowed to turn a successful cart mutation into a visible error.
  closeDialog = async () => {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    let restoreY = window.scrollY;
    const lockedTop = Number.parseFloat(document.body.style.top || '');
    if (Number.isFinite(lockedTop) && lockedTop < 0) restoreY = Math.abs(lockedTop);

    try {
      dialog.close();
    } finally {
      dialog.classList.remove('dialog-closing');
      dialog.style.animation = '';
      dialog.removeAttribute('aria-busy');

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';

      try {
        window.scrollTo(0, restoreY);
      } catch (error) {
        console.warn('[Carefone cart model switcher] Could not restore scroll position.', error);
      }

      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  #scheduleRefresh() {
    if (this.#injectQueued) return;
    this.#injectQueued = true;
    requestAnimationFrame(() => {
      this.#injectQueued = false;
      this.#refresh();
    });
  }

  #refresh() {
    this.#loadContexts();
    this.#injectControls();
  }

  #loadContexts() {
    this.#contexts.clear();
    for (const node of this.querySelectorAll('script[data-cart-model-line]')) {
      if (!(node instanceof HTMLScriptElement)) continue;
      try {
        const context = JSON.parse(node.textContent || '{}');
        if (!context.lineKey) continue;
        context.properties = this.#propertyObject(context.properties);
        this.#contexts.set(String(context.lineKey), context);
      } catch (error) {
        console.warn('[Carefone cart model switcher] Invalid line context.', error);
      }
    }
  }

  #injectControls() {
    const cartComponent = this.closest('cart-items-component');
    if (!cartComponent) return;

    for (const row of cartComponent.querySelectorAll('.cart-items__table-row[data-key]')) {
      if (!(row instanceof HTMLElement)) continue;
      const lineKey = row.dataset.key;
      if (!lineKey) continue;
      const context = this.#contexts.get(String(lineKey));
      if (!context?.currentModel) continue;

      const existing = row.querySelector('.carefone-cart-model-control');
      if (existing) continue;

      const mount = row.querySelector('.cart-items__variants-wrapper') || row.querySelector('.cart-items__details');
      if (!(mount instanceof HTMLElement)) continue;

      const wrapper = document.createElement('div');
      wrapper.className = 'cart-items__model-confirmation carefone-cart-model-control';

      const model = document.createElement('span');
      const modelLabel = document.createElement('strong');
      modelLabel.textContent = 'Model: ';
      model.append(modelLabel, document.createTextNode(context.currentModel));
      wrapper.append(model);

      if (context.currentColor) {
        const color = document.createElement('span');
        const colorLabel = document.createElement('strong');
        colorLabel.textContent = 'Color: ';
        color.append(colorLabel, document.createTextNode(context.currentColor));
        wrapper.append(color);
      }

      if (context.customDesignId && context.customiserResumeUrl) {
        const link = document.createElement('a');
        link.className = 'cart-items__change-model';
        link.href = context.customiserResumeUrl;
        link.textContent = 'Change phone model';
        wrapper.append(link);
      } else if (!context.customDesignId && Array.isArray(context.products) && context.products.length) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button-unstyled cart-items__change-model';
        button.dataset.cartModelChangeTrigger = '';
        button.dataset.lineKey = String(lineKey);
        button.dataset.dialogId = this.id;
        button.textContent = 'Change phone model';
        wrapper.append(button);
      }

      mount.append(wrapper);
    }
  }

  #handleDocumentClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-cart-model-change-trigger]') : null;
    if (!(trigger instanceof HTMLButtonElement)) return;
    if (trigger.dataset.dialogId !== this.id) return;

    const lineKey = trigger.dataset.lineKey;
    if (!lineKey) return;

    this.#loadContexts();
    const context = this.#contexts.get(String(lineKey));
    if (!context) return;

    event.preventDefault();
    this.#context = context;
    this.#lastTrigger = trigger;
    this.#clearMessage();
    this.#renderModels();
    this.showDialog();
  };

  #handleDialogClick = (event) => {
    if (!(event.target instanceof Element) || this.#busy) return;
    if (event.target.closest('[data-cart-model-close]')) return void this.closeDialog();
    if (event.target.closest('[data-cart-model-back]')) this.#showModelView();
  };

  #restoreFocus = () => {
    if (this.#lastTrigger?.isConnected) this.#lastTrigger.focus();
  };

  #renderModels() {
    const context = this.#context;
    const list = this.querySelector('[data-cart-model-list]');
    if (!context || !list) return;
    list.replaceChildren();

    const groups = new Map();
    for (const product of context.products || []) {
      if (context.currentBrand && product.brand !== context.currentBrand) continue;
      if (!product.model) continue;
      if (!groups.has(product.model)) groups.set(product.model, []);
      groups.get(product.model).push(product);
    }

    for (const [model, products] of groups) {
      const choices = this.#flattenChoices(products);
      const available = choices.filter((choice) => choice.available);
      const exact = available.find((choice) => this.#isExactChoice(choice));
      const isCurrent = model === context.currentModel;
      const soldOut = available.length === 0;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-model-dialog__choice';
      button.disabled = isCurrent || soldOut;
      if (isCurrent) button.classList.add('is-current');
      if (soldOut) button.classList.add('is-sold-out');

      const label = document.createElement('span');
      label.className = 'cart-model-dialog__choice-label';
      label.textContent = model;

      const meta = document.createElement('span');
      meta.className = 'cart-model-dialog__choice-meta';
      if (isCurrent) meta.textContent = 'Current';
      else if (soldOut) meta.textContent = 'Sold out';
      else if (exact) meta.textContent = exact.price;
      else meta.textContent = `Choose option · ${available[0].price}`;

      button.append(label, meta);
      if (exact && !isCurrent) button.addEventListener('click', () => this.#replaceLine(exact));
      else if (!isCurrent && !soldOut) button.addEventListener('click', () => this.#renderOptions(model, choices));
      list.append(button);
    }

    this.#showModelView();
  }

  #flattenChoices(products) {
    return products.flatMap((product) => (product.variants || []).map((variant) => ({
      ...variant,
      productColor: product.color || '',
      productTitle: product.title || '',
      model: product.model,
    })));
  }

  #isExactChoice(choice) {
    const context = this.#context;
    if (!context) return false;
    const colorMatches = !context.currentColor || choice.productColor === context.currentColor;
    return colorMatches && this.#sameOptions(choice.options || [], context.currentOptions || []);
  }

  #sameOptions(left, right) {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }

  #renderOptions(model, choices) {
    const title = this.querySelector('[data-cart-model-option-title]');
    const list = this.querySelector('[data-cart-model-option-list]');
    if (!title || !list) return;
    title.textContent = model;
    list.replaceChildren();

    for (const choice of choices) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-model-dialog__choice';
      button.disabled = !choice.available;
      if (!choice.available) button.classList.add('is-sold-out');

      const label = document.createElement('span');
      label.className = 'cart-model-dialog__choice-label';
      const parts = [choice.productColor, ...(choice.options || []).filter((option) => option !== 'Default Title')].filter(Boolean);
      label.textContent = parts.join(' · ') || 'Standard';

      const meta = document.createElement('span');
      meta.className = 'cart-model-dialog__choice-meta';
      meta.textContent = choice.available ? choice.price : 'Sold out';
      button.append(label, meta);
      if (choice.available) button.addEventListener('click', () => this.#replaceLine(choice));
      list.append(button);
    }

    this.querySelector('[data-cart-model-view]')?.setAttribute('hidden', '');
    this.querySelector('[data-cart-model-option-view]')?.removeAttribute('hidden');
  }

  #showModelView() {
    this.querySelector('[data-cart-model-option-view]')?.setAttribute('hidden', '');
    this.querySelector('[data-cart-model-view]')?.removeAttribute('hidden');
  }

  async #replaceLine(choice) {
    const context = this.#context;
    if (!context || this.#busy) return;

    this.#setBusy(true, 'Updating your phone model…');
    this.#clearMessage();

    let addedLineKey = null;
    let previousTargetQuantity = 0;
    let finalCart = null;
    let stage = 'reading cart';

    // Only Shopify/cart mutation failures belong in the customer-facing error
    // state. Dialog/UI cleanup runs separately after the cart transaction has
    // succeeded, so a browser-specific DOM exception cannot masquerade as a cart
    // failure.
    try {
      stage = 'reading cart';
      const beforeCart = await this.#request('cart.js');
      const previousTarget = beforeCart.items?.find((item) =>
        String(item.variant_id) === String(choice.id) &&
        this.#sameProperties(item.properties, context.properties) &&
        String(item.selling_plan_allocation?.selling_plan?.id || '') === String(context.sellingPlan || '')
      );
      previousTargetQuantity = previousTarget?.quantity || 0;

      stage = 'adding replacement model';
      const addResult = await this.#request('cart/add.js', {
        items: [{
          id: choice.id,
          quantity: context.quantity,
          properties: context.properties || {},
          ...(context.sellingPlan ? { selling_plan: context.sellingPlan } : {}),
        }],
      });

      addedLineKey = addResult.items?.[0]?.key || previousTarget?.key || null;
      if (!addedLineKey) throw new Error('The replacement item could not be identified.');

      stage = 'removing previous model';
      try {
        const sectionIds = this.#sectionIds();
        finalCart = await this.#request('cart/change.js', {
          id: context.lineKey,
          quantity: 0,
          ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
        });
      } catch (removeError) {
        stage = 'rolling back replacement';
        await this.#request('cart/change.js', { id: addedLineKey, quantity: previousTargetQuantity });
        throw removeError;
      }
    } catch (error) {
      console.error(`[Carefone cart model switcher] Failed while ${stage}.`, error);
      this.#showError(error instanceof Error ? error.message : 'Could not change the phone model. Try again.');
      this.#setBusy(false, '');
      return;
    }

    // At this point Shopify has already confirmed the cart mutation. Never show a
    // UI/browser cleanup exception as a cart error.
    try {
      await this.closeDialog();
    } catch (error) {
      console.warn('[Carefone cart model switcher] Cart changed, but dialog cleanup failed.', error);
      const dialog = this.refs.dialog;
      if (dialog instanceof HTMLDialogElement && dialog.open) {
        try { dialog.close(); } catch {}
      }
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
    }

    try {
      this.dispatchEvent(new CartUpdateEvent(finalCart, 'cart-model-change', {
        sections: finalCart?.sections,
        items: finalCart?.items,
        itemCount: finalCart?.item_count,
        source: 'cart-model-change',
        didError: false,
      }));
    } catch (error) {
      console.error('[Carefone cart model switcher] Cart changed, but local cart refresh failed.', error);
      window.location.reload();
      return;
    }

    this.#setBusy(false, '');
  }

  #sameProperties(left = {}, right = {}) {
    const normalise = (value) => Object.entries(this.#propertyObject(value))
      .filter(([, propertyValue]) => propertyValue !== null && propertyValue !== '')
      .sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(normalise(left)) === JSON.stringify(normalise(right));
  }

  #propertyObject(value) {
    if (!value || typeof value !== 'object') return {};
    if (!Array.isArray(value)) return value;
    return Object.fromEntries(value.filter((entry) => Array.isArray(entry) && entry.length >= 2));
  }

  #sectionIds() {
    return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean))];
  }

  async #request(path, body) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}${path}`, body === undefined ? undefined : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    let data;
    try {
      data = await response.json();
    } catch (error) {
      throw new Error(`Cart returned an invalid response (${response.status}).`);
    }

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not update the cart. Try again.');
    }
    return data;
  }

  #setBusy(busy, message) {
    this.#busy = busy;
    this.refs.dialog?.toggleAttribute('aria-busy', busy);
    for (const button of this.querySelectorAll('button')) {
      button.disabled = busy || button.classList.contains('is-sold-out') || button.classList.contains('is-current');
    }
    this.querySelector('[data-cart-model-loader]')?.toggleAttribute('hidden', !busy);
    const status = this.querySelector('[data-cart-model-status]');
    if (status) status.textContent = message;
  }

  #clearMessage() {
    const error = this.querySelector('[data-cart-model-error]');
    if (!error) return;
    error.textContent = '';
    error.setAttribute('hidden', '');
  }

  #showError(message) {
    const error = this.querySelector('[data-cart-model-error]');
    if (!error) return;
    error.textContent = message;
    error.removeAttribute('hidden');
  }
}

if (!customElements.get('cart-model-change-dialog')) {
  customElements.define('cart-model-change-dialog', CartModelChangeDialog);
}
